import React, { useRef, useState } from 'react';
import { Suspense } from 'react';
import dynamic from 'next/dynamic';
import { lazily } from 'react-lazily';

import styles from './verification-form.module.scss';
import { useForm } from 'react-hook-form';
import { PostErrorResponse, PostVerificationRequest } from 'services/idm';
import { CodeRequestButton } from '@login/components/code-request-button';
import SixDigitsCodeForm, { SixDigitsCodeFormHandle } from '@login/components/six-digits-code-form';
import { useAppTranslation } from 'hooks/use-translates';

const Button = dynamic(() => import('core/generic/elements/button'), {
    ssr: false,
});
const { Insert } = lazily(() => import('core/generic/elements/form'));

type VerificationForm = {
    onResendCode: () => void;
    onSubmit: (code: string) => Promise<void>;
};

export const VerificationForm = ({ onResendCode, onSubmit }: VerificationForm) => {
    const { t } = useAppTranslation();
    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm<PostVerificationRequest>();

    const [verificationCode, setVerificationCode] = useState('');
    const [triggerRenew, setTriggerRenew] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const SixDigitsCodeFormRef = useRef<SixDigitsCodeFormHandle>(null);

    const verifyEmail = async (formData: { code: string }) => {
        if (isSubmitting) return;

        if (verificationCode?.length !== 6) {
            SixDigitsCodeFormRef.current?.showErrorMessage(t('verification.code_required'));
            return;
        }

        setIsSubmitting(true);
        try {
            await onSubmit(formData.code);
        } catch (e) {
			const error = e as PostErrorResponse;

			if (error.status === 422 && error.title === 'VALIDATION_ERROR' && error.errors) {
				if (error.errors.email) {
					SixDigitsCodeFormRef.current?.showErrorMessage(error.errors.email[0]);
				} else if (error.errors.code) {
					SixDigitsCodeFormRef.current?.showErrorMessage(error.errors.code[0]);
				} else if (error.errors.mobile) {
					SixDigitsCodeFormRef.current?.showErrorMessage(error.errors.mobile[0]);
				}
			}
        } finally {
			resetForm();
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setValue('code', '');
        SixDigitsCodeFormRef.current?.reset();
    };

    const reSendCode = () => {
        setTriggerRenew(!triggerRenew);
        resetForm();
        onResendCode();
    };

    const outputVerificationCode = (code: string) => {
        setValue('code', code);
        setVerificationCode(code);
    };

    return (
        <Suspense>
            <form
                onSubmit={handleSubmit(verifyEmail)}
                id="form-profile"
                className={styles['verify-email-form']}
                noValidate
            >
                <div className={styles['form-box--input']}>
                    <p className="label-medium">{t('verification.enter_code_label')}</p>

                    <SixDigitsCodeForm
                        outputCode={outputVerificationCode}
                        ref={SixDigitsCodeFormRef}
                    />

                    <div className={styles['input-hidden']}>
                        <Insert
                            type="hidden"
                            {...register('code')}
                            defaultValue=""
                            maxLength={6}
                            minLength={6}
                            id="verificationCode"
                            required={true}
                            error={errors.code?.message}
                            disabled={false}
                        />
                    </div>
                </div>
                <div className={styles['verify-email-form-btns-set']}>
                    <Button
                        type="submit"
                        label={
                            isSubmitting
                                ? t('verification.verifying_button_label')
                                : t('verification.submit_button_label')
                        }
                        variant="secondary"
                        fullWidth={true}
                        isLoading={isSubmitting}
                        dimension="large"
                    />
                    <CodeRequestButton onClick={reSendCode} triggerRenew={triggerRenew} />
                </div>
            </form>
        </Suspense>
    );
};

export default VerificationForm;
